/**
 * THE DIALOG PRIMITIVE (P36) — reference §10, and the answer to S1-07.
 *
 * =========================================================================
 * WHAT WAS MISSING
 * =========================================================================
 * `ConfirmModal` had none of the five behaviours a modal owes: no
 * `role="dialog"`, no `aria-modal`, no `aria-labelledby`, **no Escape, no focus
 * move in, no focus trap and no focus return**. It is used at 11 call sites,
 * several of which ask whether to withdraw a waiting draft or cancel an open
 * Sales request — decisions a keyboard user could reach and then not leave.
 *
 * Meanwhile `ColumnFilter.tsx` and `ColumnChooser.tsx` handled Escape, a real
 * `<button>`, `aria-expanded`, `aria-label` and `role="dialog"` correctly. The
 * kit disagreed with itself, and the better half is the model this file
 * follows.
 *
 * =========================================================================
 * FIVE BEHAVIOURS, AND THE ONE THAT IS DELIBERATELY ABSENT
 * =========================================================================
 * 1. `role="dialog"` + `aria-modal="true"` + `aria-labelledby` pointing at the
 *    real `<h2>` the header renders.
 * 2. **Escape cancels.** Bound on the document, like `ColumnFilter`'s, so it
 *    works whatever is focused inside.
 * 3. **Focus moves in on open** — into the first EMPTY field if the dialog has
 *    one (reference §10), otherwise the first focusable element, otherwise the
 *    dialog itself, which carries `tabIndex={-1}` so that last case still has
 *    somewhere to land.
 * 4. **Focus is trapped**: Tab from the last focusable wraps to the first,
 *    Shift+Tab from the first wraps to the last.
 * 5. **Focus returns to the opener** on close — the element that was active
 *    when the dialog mounted, captured then and not looked up again.
 *
 * Absent on purpose: **a click on the backdrop does not close.** Nine of the
 * eleven call sites ask a question whose answer changes a fact on a live
 * ticket, and half of those state what the click will withdraw. A stray click
 * beside the card is not an answer to that, and Escape plus a named Cancel
 * button are two ways out already. `ColumnFilter`'s popover does close on an
 * outside click, and that is the right rule for a menu — nothing is lost.
 *
 * `prefers-reduced-motion` is not here, and that is not an oversight: the app
 * has exactly one kind of motion, a spinner, and the dialog adds none.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Button } from './Button';

const FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Deliberately NOT filtered on `offsetParent` or `getComputedStyle`. The
 * obvious visibility check is a layout read, and a dialog is opened in jsdom by
 * every test that operates it — where there is no layout, so every element
 * reads as hidden and the trap silently traps nothing. The selector already
 * excludes what actually matters (`[disabled]`, `tabindex="-1"`, hidden
 * inputs); a subtree the dialog itself hides is excluded by `hidden` /
 * `aria-hidden`, which are attributes and not geometry.
 */
function focusableIn(root: HTMLElement): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter(el => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true');
}

export interface DialogProps {
    /** Rendered as the `<h2>` the dialog is labelled by. */
    title: string;
    /** Escape, the Cancel button and the close button all arrive here. */
    onClose: () => void;
    /** The buttons, in reading order. The primary or destructive one goes last. */
    footer?: ReactNode;
    children: ReactNode;
    /** Sits left of the title — the ConfirmModal's warning glyph. */
    icon?: ReactNode;
}

export function Dialog({ title, onClose, footer, children, icon }: DialogProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    const titleId = useId();

    // Captured in a layout-independent effect that runs once: by the time a
    // later render happens, the opener may already have been unmounted by the
    // very state change that opened this dialog.
    useEffect(() => {
        openerRef.current = document.activeElement as HTMLElement | null;
        const panel = panelRef.current;
        if (panel) {
            const items = focusableIn(panel);
            const firstEmptyField = items.find(
                el => (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) && el.value === '',
            );
            (firstEmptyField ?? items[0] ?? panel).focus();
        }
        return () => {
            // A focus return into an element the page has since removed throws
            // nothing but lands nowhere, so the guard is `isConnected`.
            const opener = openerRef.current;
            if (opener && opener.isConnected) opener.focus();
        };
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
                return;
            }
            if (e.key !== 'Tab') return;
            const panel = panelRef.current;
            if (!panel) return;
            const items = focusableIn(panel);
            if (items.length === 0) {
                e.preventDefault();
                panel.focus();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            const active = document.activeElement;
            if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            } else if (e.shiftKey && (active === first || active === panel)) {
                e.preventDefault();
                last.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className="w-full max-w-md rounded-card bg-surface p-6 shadow-overlay focus:outline-none"
            >
                <div className="flex items-start gap-3">
                    {icon}
                    <div className="flex-1">
                        <h2 id={titleId} className="text-lg font-semibold text-ink">{title}</h2>
                        <div className="mt-1 text-sm text-body">{children}</div>
                    </div>
                </div>
                {footer && <div className="mt-5 flex justify-end gap-2">{footer}</div>}
            </div>
        </div>
    );
}

/**
 * The Cancel button every dialog has. Exported so the label — the one thing
 * that varies — is the only thing a call site writes.
 */
export function DialogCancel({ label = 'Cancel', onClick }: { label?: string; onClick: () => void }) {
    return <Button variant="secondary" size="sm" onClick={onClick}>{label}</Button>;
}
